import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
    isValidElement,
} from 'react'
import { Icon } from '../Icons'
import {
    InputContainer,
    StyledInputWrapper,
    StyleBeforeIcon,
    StyledInput,
    StyleAfterIcon,
    Label,
} from './style'
import IMask from 'imask/esm/imask'
import 'imask/esm/masked/pattern'
import { InputMask } from 'imask/esm/index'

interface InputProps extends React.HTMLProps<HTMLInputElement> {
    label?: string
    type?: 'password' | 'text' | 'email' | 'phone' | 'number' | 'date' | 'time'
    placeholder?: string
    $beforeIcon?: string | React.ReactElement
    $afterIcon?: string | React.ReactElement
    className?: string
    required?: boolean
    disabled?: boolean
    afterIconOnClick?: () => void
    clearInput?: () => void
    name?: string
    maskConfig?: any
    fieldProps?: any
    onAccept?: (value: string) => void
    error?: string
}

export const Input: React.FC<InputProps> = ({
    label,
    type = 'text',
    $beforeIcon,
    $afterIcon,
    className,
    required,
    disabled = false,
    afterIconOnClick,
    clearInput,
    name,
    fieldProps = {},
    maskConfig,
    onAccept,
    error,
    ...props
}) => {
    const [inputType, setInputType] = useState(type === 'phone' ? 'text' : type)

    const inputRef = useRef<HTMLInputElement>(null)
    const maskRef = useRef<InputMask | null>(null)

    useEffect(() => {
        if (inputRef.current && maskConfig?.mask) {
            maskRef.current = IMask(inputRef.current, maskConfig)

            const handleAccept = () => {
                const value = maskRef.current!.value
                onAccept?.(value)

                if (fieldProps.onChange) {
                    const event = {
                        target: { value, name },
                    }
                    fieldProps.onChange(event)
                }
            }
            maskRef.current.on('accept', handleAccept)

            if (fieldProps.value !== undefined) {
                maskRef.current.value = String(fieldProps.value)
                maskRef.current.updateValue()
            }

            return () => {
                maskRef.current?.off('accept', handleAccept)
                maskRef.current?.destroy()
            }
        }
    }, [maskConfig, onAccept, fieldProps.onChange, name, fieldProps.value])

    useEffect(() => {
        if (maskRef.current && fieldProps.value !== undefined) {
            const stringValue = String(fieldProps.value)
            if (maskRef.current.value !== stringValue) {
                maskRef.current.value = stringValue
                maskRef.current.updateValue()
            }
        }
    }, [fieldProps.value])

    const showClearIcon =
        !disabled &&
        fieldProps.value &&
        (typeof fieldProps.value === 'string'
            ? fieldProps.value.length > 0
            : typeof fieldProps.value === 'number'
                ? fieldProps.value > 0
                : false)

    useEffect(() => {
        setInputType(type === 'phone' ? 'text' : type)
    }, [type])

    const toggleInputType = useCallback(() => {
        setInputType(prevType =>
            prevType === 'password' ? 'text' : 'password',
        )
    }, [])

    return (
        <InputContainer className={className}>
            {label && (
                <Label>
                    {required && <span className='required'>*</span>}
                    {label}
                </Label>
            )}
            <StyledInputWrapper className='styledWrapperInput'>
                {$beforeIcon && typeof $beforeIcon === 'string' && (
                    <StyleBeforeIcon className='styledBeforeIcon'>
                        <Icon name={$beforeIcon} />
                    </StyleBeforeIcon>
                )}
                {$beforeIcon && isValidElement($beforeIcon) && (
                    <StyleBeforeIcon className='styledBeforeIcon'>
                        {$beforeIcon}
                    </StyleBeforeIcon>
                )}
                <StyledInput
                    {...fieldProps}
                    ref={inputRef}
                    disabled={disabled}
                    $beforeIcon={$beforeIcon}
                    $afterIcon={$afterIcon}
                    type={inputType}
                    // pattern={inputType === 'number' ? '[0-9]*' : undefined}
                    {...props}
                    className='styledInput'
                    name={name}
                />
                {$afterIcon && isValidElement($afterIcon) && (
                    <StyleAfterIcon>{$afterIcon}</StyleAfterIcon>
                )}
                {$afterIcon && type === 'password' ? (
                    <StyleAfterIcon onClick={toggleInputType}>
                        {inputType === 'password' ? (
                            <Icon name='hidden' />
                        ) : (
                            <Icon name='show' />
                        )}
                    </StyleAfterIcon>
                ) : showClearIcon && $afterIcon === 'clearInput' ? (
                    <StyleAfterIcon onClick={clearInput || afterIconOnClick}>
                        <Icon name={$afterIcon} />
                    </StyleAfterIcon>
                ) : $afterIcon &&
                  typeof $afterIcon === 'string' &&
                  $afterIcon !== 'clearInput' ? (
                    <StyleAfterIcon onClick={afterIconOnClick}>
                        <Icon name={$afterIcon} />
                    </StyleAfterIcon>
                ) : null}
            </StyledInputWrapper>
            {error && <span className='error-message'>{error}</span>}
        </InputContainer>
    )
}
